// Permission + PushManager subscription flow. Turns the VAPID public key into the
// binary applicationServerKey the browser expects, asks for permission, and
// returns the resulting PushSubscription (or unsubscribes an existing one).

import { VAPID_PUBLIC_KEY } from './pushConfig'
import { getReadyRegistration } from './register'

// The VAPID public key is base64url; PushManager.subscribe wants a BufferSource
// backed by a concrete ArrayBuffer (not a SharedArrayBuffer-compatible view).
function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(base64)
  const output = new Uint8Array(new ArrayBuffer(raw.length))
  for (let i = 0; i < raw.length; i++) {
    output[i] = raw.charCodeAt(i)
  }
  return output
}

// Prompts for notification permission (if not already decided) and subscribes the
// device. Resolves to null if the user denies permission — callers surface that as
// a blocked state rather than an error.
export async function requestPermissionAndSubscribe(): Promise<PushSubscription | null> {
  const permission = await Notification.requestPermission()
  if (permission !== 'granted') return null

  const registration = await getReadyRegistration()
  // Reuse an existing subscription if the browser already has one for this key.
  const existing = await registration.pushManager.getSubscription()
  if (existing) return existing

  return registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
  })
}

// Reads the current subscription for this device without prompting.
export async function getCurrentSubscription(): Promise<PushSubscription | null> {
  if (!('serviceWorker' in navigator)) return null
  const registration = await getReadyRegistration()
  return registration.pushManager.getSubscription()
}

// Tears down the browser-side subscription. Returns the unsubscribed subscription
// (or null if there was none) so the caller can clean up its Firestore doc.
export async function unsubscribe(): Promise<PushSubscription | null> {
  const subscription = await getCurrentSubscription()
  if (!subscription) return null
  await subscription.unsubscribe()
  return subscription
}
